import React, { useState, useRef, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, Move, RotateCcw, Check, Sparkles, User, Crop, X, Loader2, AlertTriangle } from "lucide-react";
import { API_BASE } from "@/lib/api-client-react";

interface InlinePhotoAdjusterProps {
  imageSrc: string;
  onSave: (croppedBlob: Blob, croppedDataUrl: string) => Promise<void> | void;
  onCancel: () => void;
  isUploading?: boolean;
}

export function InlinePhotoAdjuster({
  imageSrc,
  onSave,
  onCancel,
  isUploading = false,
}: InlinePhotoAdjusterProps) {
  const [zoom, setZoom] = useState(1.15);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [localBlobUrl, setLocalBlobUrl] = useState<string>("");
  const [isFetchingBlob, setIsFetchingBlob] = useState(false);
  const [imgDimensions, setImgDimensions] = useState<{ width: number; height: number }>({ width: 208, height: 208 });

  const dragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const offsetStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const imgRef = useRef<HTMLImageElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  // Normalize imageSrc to handle relative backend paths correctly
  const resolvedSrc = useMemo(() => {
    if (!imageSrc) return "";
    if (imageSrc.startsWith("data:") || imageSrc.startsWith("blob:") || imageSrc.startsWith("http://") || imageSrc.startsWith("https://")) {
      return imageSrc;
    }
    const cleanBase = API_BASE ? API_BASE.replace(/\/v1|\/api/, '') : "http://localhost:4000";
    const cleanPath = imageSrc.startsWith('/') ? imageSrc : `/${imageSrc}`;
    return `${cleanBase}${cleanPath}`;
  }, [imageSrc]);

  // Convert HTTP/HTTPS remote URLs to local blob: URLs to prevent Canvas Tainting (CORS SecurityError on toBlob)
  useEffect(() => {
    let isMounted = true;
    let createdUrl = "";

    setZoom(1.15);
    setOffsetX(0);
    setOffsetY(0);
    setImgLoaded(false);
    setImgError(false);

    if (!resolvedSrc) {
      setLocalBlobUrl("");
      return;
    }

    if (resolvedSrc.startsWith("data:") || resolvedSrc.startsWith("blob:")) {
      setLocalBlobUrl(resolvedSrc);
      return;
    }

    setIsFetchingBlob(true);
    fetch(resolvedSrc, { mode: "cors" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (!isMounted) return;
        createdUrl = URL.createObjectURL(blob);
        setLocalBlobUrl(createdUrl);
      })
      .catch((err) => {
        console.warn("Could not convert image to blob, falling back to direct URL:", err);
        if (isMounted) setLocalBlobUrl(resolvedSrc);
      })
      .finally(() => {
        if (isMounted) setIsFetchingBlob(false);
      });

    return () => {
      isMounted = false;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [resolvedSrc]);

  const VIEWPORT_SIZE = 208; // Exact size of circular preview box (w-52 h-52 = 208px)

  // Calculate dynamic max allowed offsets so image is ALWAYS 100% covering container with ZERO empty space
  const maxOffsets = useMemo(() => {
    const naturalW = imgDimensions.width || VIEWPORT_SIZE;
    const naturalH = imgDimensions.height || VIEWPORT_SIZE;
    const aspect = naturalW / naturalH;

    let baseW = VIEWPORT_SIZE;
    let baseH = VIEWPORT_SIZE;

    if (aspect > 1) {
      baseH = VIEWPORT_SIZE;
      baseW = VIEWPORT_SIZE * aspect;
    } else {
      baseW = VIEWPORT_SIZE;
      baseH = VIEWPORT_SIZE / aspect;
    }

    const renderedW = baseW * zoom;
    const renderedH = baseH * zoom;

    const maxX = Math.max(0, (renderedW - VIEWPORT_SIZE) / 2);
    const maxY = Math.max(0, (renderedH - VIEWPORT_SIZE) / 2);

    return { maxX, maxY };
  }, [imgDimensions, zoom]);

  // Clamp offsets so image cannot be pushed outside the circular viewport frame
  const clampedX = Math.min(Math.max(offsetX, -maxOffsets.maxX), maxOffsets.maxX);
  const clampedY = Math.min(Math.max(offsetY, -maxOffsets.maxY), maxOffsets.maxY);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImgLoaded(true);
    setImgError(false);
    if (img.naturalWidth && img.naturalHeight) {
      setImgDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    }
  };

  const handleImageError = () => {
    setImgError(true);
    setImgLoaded(false);
  };

  // Mouse Drag Handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!imgLoaded || imgError) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    offsetStartRef.current = { x: clampedX, y: clampedY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setOffsetX(offsetStartRef.current.x + dx);
    setOffsetY(offsetStartRef.current.y + dy);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch Handlers for Mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!imgLoaded || imgError) return;
    if (e.touches.length === 1) {
      setIsDragging(true);
      dragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      offsetStartRef.current = { x: clampedX, y: clampedY };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - dragStartRef.current.x;
    const dy = e.touches[0].clientY - dragStartRef.current.y;
    setOffsetX(offsetStartRef.current.x + dx);
    setOffsetY(offsetStartRef.current.y + dy);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Helper to render image onto canvas and get Blob cleanly
  const drawAndExportCanvas = (sourceImg: HTMLImageElement): Promise<{ blob: Blob; dataUrl: string }> => {
    return new Promise((resolve, reject) => {
      const CANVAS_SIZE = 400; // Output high-res square
      const canvas = document.createElement("canvas");
      canvas.width = CANVAS_SIZE;
      canvas.height = CANVAS_SIZE;
      const ctx = canvas.getContext("2d");

      if (!ctx) return reject(new Error("Canvas 2D context not available"));

      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

      const naturalW = sourceImg.naturalWidth || VIEWPORT_SIZE;
      const naturalH = sourceImg.naturalHeight || VIEWPORT_SIZE;
      const baseScale = Math.max(VIEWPORT_SIZE / naturalW, VIEWPORT_SIZE / naturalH);

      const renderedW = naturalW * baseScale * zoom;
      const renderedH = naturalH * baseScale * zoom;

      const drawX = (VIEWPORT_SIZE - renderedW) / 2 + clampedX;
      const drawY = (VIEWPORT_SIZE - renderedH) / 2 + clampedY;

      const scaleFactor = CANVAS_SIZE / VIEWPORT_SIZE;

      const targetW = renderedW * scaleFactor;
      const targetH = renderedH * scaleFactor;
      const targetX = drawX * scaleFactor;
      const targetY = drawY * scaleFactor;

      ctx.drawImage(sourceImg, targetX, targetY, targetW, targetH);

      try {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
              resolve({ blob, dataUrl });
            } else {
              reject(new Error("Canvas blob generation failed"));
            }
          },
          "image/jpeg",
          0.92
        );
      } catch (err) {
        reject(err);
      }
    });
  };

  // Apply Canvas Crop & Generate High-Res Centered Image
  const handleApplyCrop = async () => {
    if (!imgRef.current) return;
    try {
      // 1. First attempt direct canvas export
      const result = await drawAndExportCanvas(imgRef.current);
      await onSave(result.blob, result.dataUrl);
    } catch (err) {
      console.warn("Direct canvas export failed (tainted canvas), retrying with fresh anonymous image load:", err);
      // 2. Safe Fallback: Load image via Image() with crossOrigin anonymous or blob fetch
      try {
        const fetchRes = await fetch(resolvedSrc, { mode: "cors" });
        const fetchBlob = await fetchRes.blob();
        const tempBlobUrl = URL.createObjectURL(fetchBlob);

        const tempImg = new Image();
        tempImg.crossOrigin = "anonymous";
        tempImg.onload = async () => {
          try {
            const fallbackResult = await drawAndExportCanvas(tempImg);
            URL.revokeObjectURL(tempBlobUrl);
            await onSave(fallbackResult.blob, fallbackResult.dataUrl);
          } catch (e: any) {
            URL.revokeObjectURL(tempBlobUrl);
            alert("Failed to process profile image. Please try uploading a different photo.");
          }
        };
        tempImg.onerror = () => {
          URL.revokeObjectURL(tempBlobUrl);
          alert("Failed to process profile image.");
        };
        tempImg.src = tempBlobUrl;
      } catch (e: any) {
        alert("Failed to save centered photo: " + (e.message || "Security restriction"));
      }
    }
  };

  const sliderMinY = maxOffsets.maxY > 0 ? -Math.ceil(maxOffsets.maxY) : -30;
  const sliderMaxY = maxOffsets.maxY > 0 ? Math.ceil(maxOffsets.maxY) : 30;

  return (
    <div className="bg-muted/40 border border-primary/20 rounded-2xl p-5 space-y-4 shadow-sm my-3 animate-in fade-in slide-in-from-top-2 duration-200">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-primary/10 rounded-lg text-primary">
            <Crop className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-foreground">Adjust & Center Profile Photo</h4>
            <p className="text-xs text-muted-foreground">Drag image to position face in center and use zoom controls.</p>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          onClick={onCancel}
          disabled={isUploading}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex flex-col md:flex-row items-center gap-6 pt-1">
        {/* Circular Crop Viewport Frame */}
        <div className="flex flex-col items-center shrink-0">
          <div className="relative">
            <div
              ref={previewRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              className={`w-52 h-52 rounded-full border-4 border-primary shadow-xl overflow-hidden relative cursor-grab active:cursor-grabbing bg-slate-950 select-none flex items-center justify-center transition-shadow ${
                isDragging ? "ring-4 ring-primary/40" : ""
              }`}
            >
              {(isFetchingBlob || (!imgLoaded && !imgError && localBlobUrl)) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/90 text-white gap-2 z-10">
                  <Loader2 className="w-7 h-7 animate-spin text-primary" />
                  <span className="text-xs font-medium">Loading image...</span>
                </div>
              )}

              {imgError ? (
                <div className="p-4 text-center text-rose-400 text-xs flex flex-col items-center gap-2">
                  <AlertTriangle className="w-8 h-8 text-rose-500" />
                  <span>Failed to load image preview.</span>
                </div>
              ) : localBlobUrl ? (
                <img
                  ref={imgRef}
                  src={localBlobUrl}
                  alt="Profile Adjust Preview"
                  crossOrigin="anonymous"
                  draggable={false}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  className="w-full h-full object-cover pointer-events-none origin-center"
                  style={{
                    transform: `translate(${clampedX}px, ${clampedY}px) scale(${zoom})`,
                    transition: isDragging ? "none" : "transform 0.1s ease-out",
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <User className="w-16 h-16" />
                </div>
              )}
            </div>

            <div className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 bg-background border border-border text-foreground px-3 py-0.5 rounded-full text-[11px] font-semibold flex items-center gap-1.5 shadow-md whitespace-nowrap">
              <Move className="w-3 h-3 text-primary animate-pulse" /> Drag image to position
            </div>
          </div>
        </div>

        {/* Controls & Presets */}
        <div className="flex-1 space-y-4 w-full">
          {/* Quick Presets */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground block">Quick Alignments</label>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setZoom(1.3);
                  setOffsetX(0);
                  setOffsetY(-12);
                }}
                className="text-xs h-8 rounded-xl px-3 bg-background"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5 text-amber-500" /> Center Face
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setZoom(1.0);
                  setOffsetX(0);
                  setOffsetY(0);
                }}
                className="text-xs h-8 rounded-xl px-3 bg-background"
              >
                Fit Image
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setZoom(1.15);
                  setOffsetX(0);
                  setOffsetY(0);
                }}
                className="text-xs h-8 rounded-xl px-2.5 text-muted-foreground"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
              </Button>
            </div>
          </div>

          {/* Zoom Slider */}
          <div className="space-y-1.5 bg-background p-3 rounded-xl border border-border">
            <div className="flex items-center justify-between text-xs font-semibold">
              <span className="flex items-center gap-1.5 text-foreground">
                <ZoomIn className="w-3.5 h-3.5 text-primary" /> Zoom Level
              </span>
              <span className="text-muted-foreground font-mono">{Math.round(zoom * 100)}%</span>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <ZoomOut className="w-4 h-4 text-muted-foreground shrink-0" />
              <input
                type="range"
                min={1.0}
                max={3.0}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full h-1.5 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <ZoomIn className="w-4 h-4 text-muted-foreground shrink-0" />
            </div>
          </div>

          {/* Vertical Y-Offset Slider */}
          <div className="space-y-1.5 bg-background p-3 rounded-xl border border-border">
            <div className="flex items-center justify-between text-xs font-semibold">
              <span className="text-foreground">Vertical Position (Up / Down)</span>
              <span className="text-muted-foreground font-mono">{Math.round(clampedY)}px</span>
            </div>
            <div className="pt-1">
              <input
                type="range"
                min={sliderMinY}
                max={sliderMaxY}
                step={1}
                value={clampedY}
                onChange={(e) => setOffsetY(parseFloat(e.target.value))}
                className="w-full h-1.5 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isUploading}
              className="rounded-xl h-9 text-xs"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleApplyCrop}
              disabled={isUploading || imgError}
              className="rounded-xl h-9 text-xs font-semibold gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
            >
              {isUploading ? (
                "Uploading..."
              ) : (
                <>
                  <Check className="w-4 h-4" /> Save Centered Photo
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
